"use client";

import React from "react";
import { UserPlus, FileCheck, Wallet, ArrowRight, CircleCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const steps = [{
  title: "Precision Engine",
  description: "Our proprietary algorithms ensure 100% accuracy across complex financial and mathematical models for reliable results.",
  icon: UserPlus,
  benefits: ["Error-free logic", "Real-time processing"]
}, {
  title: "Privacy First",
  description: "Your data never leaves your browser. We prioritize security with client-side processing and zero-tracking policies.",
  icon: FileCheck,
  benefits: ["No data storage", "Encrypted sessions"]
}, {
  title: "Global Standards",
  description: "Built to comply with international formatting and mathematical standards, supporting multi-currency and metric systems.",
  icon: Wallet,
  benefits: ["ISO compliant", "Universal formatting"]
}];
export default function DriverEmpowerment() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: timelineRef,
    isVisible: isTimelineVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="224768" id="why-trust-our-tools" className="bg-background text-foreground py-24 px-4 md:px-8 relative overflow-hidden">
      {}
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="max-w-5xl mx-auto relative z-10">
        {}
        <div ref={headerRef} className={`text-center max-w-2xl mx-auto mb-20 transition-all duration-1000 ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="outline" className="mb-6 border-primary/20 text-primary bg-primary/5 px-4 py-1.5 text-sm font-default">
            The Calculo Standard
          </Badge>
          <h2 className="text-4xl md:text-5xl font-bold font-default mb-6 tracking-tight text-foreground">
            Engineered for <span className="text-primary">Absolute Precision</span>
          </h2>
          <p className="text-lg text-muted-foreground mb-8 font-default">
            Discover why thousands of professionals trust CALCULO for their daily operations. Our tools are built with a focus on speed, security, and mathematical integrity.
          </p>
          <Badge variant="default" className="text-base px-6 py-2 shadow-lg shadow-primary/10 bg-primary text-primary-foreground font-default">
            Verified Accuracy: 99.99%
          </Badge>
        </div>

        {}
        <div ref={timelineRef} className={`relative transition-all duration-1000 delay-200 ${isTimelineVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          {}
          <div className="absolute left-6 md:left-1/2 top-4 bottom-4 w-px bg-gradient-to-b from-primary/30 via-border to-transparent md:-translate-x-1/2" />

          <div className="space-y-12 md:space-y-24">
            {steps.map((step, i) => {
            const Icon = step.icon;
            const isEven = i % 2 === 0;
            return <div key={i} data-index={i} className="relative flex flex-col md:flex-row items-start md:items-center group">
                  {}
                  <div className="absolute left-6 md:left-1/2 top-6 md:top-1/2 w-12 h-12 -translate-x-1/2 md:-translate-y-1/2 rounded-full bg-background border-2 border-primary flex items-center justify-center z-10 shadow-sm transition-transform duration-500 group-hover:scale-110">
                    <Icon className="size-5 text-primary" />
                  </div>

                  {}
                  <div className={`w-full md:w-1/2 pl-20 md:pl-0 ${isEven ? "md:pr-16 md:text-right" : "md:pl-16 md:ml-auto"}`}>
                    <Card className={`bg-card backdrop-blur-none border-border transition-all duration-500 ease-out hover:-translate-y-2 hover:border-primary/40 hover:shadow-xl hover:shadow-primary/5 overflow-hidden relative ${isEven ? "md:items-end" : "md:items-start"}`}>
                      {}
                      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      
                      <CardHeader className="relative z-10">
                        <div className={`flex items-center gap-4 mb-2 ${isEven ? "md:justify-end" : "md:justify-start"}`}>
                          <span className="text-5xl font-black text-foreground/5 font-mono">
                            0{i + 1}
                          </span>
                          <CardTitle className="text-xl text-foreground font-default">
                            {step.title}
                          </CardTitle>
                        </div>
                        <CardDescription className="text-sm text-muted-foreground font-default">
                          {step.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="relative z-10">
                        <ul className={`flex flex-col gap-2 ${isEven ? "md:items-end" : "md:items-start"}`}>
                          {step.benefits.map((benefit, j) => <li key={j} data-index={j} className="flex items-center gap-2 text-sm text-muted-foreground font-default">
                              {!isEven && <CircleCheck className="size-4 text-primary" />}
                              <span>{benefit}</span>
                              {isEven && <CircleCheck className="size-4 text-primary" />}
                            </li>)}
                        </ul>
                      </CardContent>
                    </Card>
                  </div>
                </div>;
          })}
          </div>
        </div>

        {}
        <div className="mt-24 text-center">
          <Button size="lg" className="rounded-full px-8 h-14 text-lg font-semibold group bg-primary text-primary-foreground hover:bg-primary/90 font-default" onClick={() => window.location.href = "/HomePage"}>
            Explore All Calculators
            <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
          </Button>
          <p className="mt-4 text-sm text-muted-foreground font-default">
            Join over 500,000 monthly users relying on CALCULO precision.
          </p>
        </div>
      </div>
    </section>;
}